import React from 'react';

/**
 * Renders an assistant message that uses light markdown-ish structure.
 *
 * The API sends these as plain text with "- " list items and a label line ending
 * in ":" introducing each list, e.g.
 *
 *   You are assessing the internal talent audit of Product Designer…
 *
 *   The primary concerns include:
 *   - Communication skills are lacking.
 *   - Collaboration with team members is insufficient.
 *
 * Rendering that verbatim gives flat hyphenated lines with collapsed blank lines.
 * This turns it into bold section labels and real bullet lists.
 */

const BULLET = /^\s*[-–—•*]\s+/;

type Block =
  | { type: 'paragraph'; text: string }
  | { type: 'list'; label?: string; items: string[] };

export const parseMessageBlocks = (text: string): Block[] => {
  const blocks: Block[] = [];
  let items: string[] = [];
  let label: string | undefined;

  const flushList = () => {
    if (items.length > 0) {
      blocks.push({ type: 'list', label, items });
      items = [];
    }
    label = undefined;
  };

  for (const raw of text.split('\n')) {
    const line = raw.trim();

    if (!line) {
      flushList();
      continue;
    }

    if (BULLET.test(line)) {
      // The line just above a list ("The primary concerns include:") is its
      // heading, not a paragraph of its own.
      if (items.length === 0) {
        const previous = blocks[blocks.length - 1];
        if (previous?.type === 'paragraph' && previous.text.endsWith(':')) {
          label = previous.text;
          blocks.pop();
        }
      }
      items.push(line.replace(BULLET, '').trim());
      continue;
    }

    flushList();
    blocks.push({ type: 'paragraph', text: line });
  }

  flushList();
  return blocks;
};

/** True when the text has list structure worth formatting. */
export const hasMessageStructure = (text: string) =>
  text.split('\n').some((line) => BULLET.test(line));

const FormattedMessage: React.FC<{ text: string }> = ({ text }) => {
  const blocks = parseMessageBlocks(text);

  return (
    <div className="flex flex-col gap-3">
      {blocks.map((block, index) =>
        block.type === 'list' ? (
          <div key={index} className="flex flex-col gap-1.5">
            {block.label && <p className="font-semibold">{block.label}</p>}
            <ul className="list-disc pl-5 flex flex-col gap-1">
              {block.items.map((item, itemIndex) => (
                <li key={itemIndex}>{item}</li>
              ))}
            </ul>
          </div>
        ) : (
          <p key={index}>{block.text}</p>
        )
      )}
    </div>
  );
};

export { FormattedMessage };
